import React, { FC, Fragment } from 'react';
import { Typography } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { Dimensions, EmptyState, IEmptyState, Loaders, PaddedPaper } from 'components';
import { InfiniteQuery } from 'types/app';
import { ContentFragment, SearchContentQueryVariables, UserFeedFollowGroupFragment } from 'api';
import { DimensionsProvider, QueryVariablesProvider } from 'providers';
import { FeedListItem } from 'features/content';

interface Props extends InfiniteQuery<ContentFragment> {
  emptyStateName: keyof IEmptyState;
  selectedGroup: UserFeedFollowGroupFragment;
  variables: SearchContentQueryVariables;
}

const LOADER_HEIGHT = 32;

const ModeratorFeed: FC<Props> = ({
  emptyStateName,
  selectedGroup,
  variables,
  results,
  sentinelRef,
  isEmpty,
  isExhausted,
  isInitialLoad,
}) => {
  const { t } = useTranslation();

  function handleFeedError() {
    return (
      <PaddedPaper>
        <Typography>{t('errors:feed')}</Typography>
      </PaddedPaper>
    );
  }

  function renderLoadMoreLoader() {
    if (isEmpty || isInitialLoad || isExhausted) {
      return <div style={{ height: LOADER_HEIGHT }} />;
    }

    return <Loaders.Standard height={LOADER_HEIGHT} />;
  }

  function renderBody() {
    if (isInitialLoad) {
      return <Loaders.Feed />;
    }

    if (isEmpty) {
      return <EmptyState fullWidth name={emptyStateName} />;
    }

    return (
      <Fragment>
        {results.map(result => (
          <ErrorBoundary key={result.content_id} fallback={handleFeedError()}>
            <FeedListItem content={result} selectedGroup={selectedGroup} />
          </ErrorBoundary>
        ))}
      </Fragment>
    );
  }

  return (
    <Dimensions>
      {dimensions => (
        <DimensionsProvider value={dimensions}>
          <QueryVariablesProvider value={variables}>
            {renderBody()}
            <div ref={sentinelRef}>{renderLoadMoreLoader()}</div>
          </QueryVariablesProvider>
        </DimensionsProvider>
      )}
    </Dimensions>
  );
};

export default ModeratorFeed;
